import React, { useState, useCallback, useEffect } from "react";
import { useHistory } from "react-router-dom";
// nodejs library that concatenates classes
import classNames from "classnames";
import Select from "react-select";
import DateFilterButton from "../DateFilterButton/DateFilterButton";

// reactstrap components
import {
  Button,
  Col,
  Collapse,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  UncontrolledDropdown,
  NavbarBrand,
  Navbar,
  NavLink,
  Nav,
  NavItem,
  Container,
  UncontrolledTooltip,
} from "reactstrap";

import { useAuth } from "hooks/auth";
import { useCompanies } from "hooks/companies";
import { useTheme } from "hooks/theme";
import AppSwitcher from "components/AppSwitcher/AppSwitcher";
import AlertasSino from "components/AlertasSino";
import AjudaTela, { useTutorialDaTela } from "components/AjudaTela";
import ProductBreadcrumb from "components/ProductBreadcrumb/ProductBreadcrumb";
import UserAvatar from "components/UserAvatar/UserAvatar";
import "./AdminNavbar.css";

// PO1-1975: paths atualizados após prefixo por produto. Inclui o legado
// `/app/dashboard` pra não quebrar caso o Redirect ainda esteja resolvendo.
const DATE_FILTER_ENABLED_PATHS = new Set([
  "/app/flow/dashboard",
  "/app/flow/meu-painel",
  "/app/gobid/meu-painel",
  "/app/trackfy/meu-painel",
  "/app/gobid/dashboard",
  "/app/trackfy/dashboard",
  "/app/everlog/torre-controle",
  "/app/dashboard",
  // PO1-2821: Entregas Pendentes é recortada por período igual aos dashboards.
  // Sem entrar aqui, o botão "Período" não aparecia e a tela ficava presa na
  // janela padrão de 90 dias, sem nenhum jeito de mudar — embarcador com dado
  // mais antigo (a Duchen, por exemplo) via a tela vazia e sem saída.
  "/app/trackfy/operacao/entregas-pendentes",
  // PO1-3649: o funil da Rede Everlog é recortado por período.
  "/app/everlog/rede-everlog-funil",
]);

const SELECT_THEME_CLASS = {
  primary: "primary",
  blue: "info",
  green: "success",
  orange: "warning",
  red: "danger",
};

const AdminNavbar = (props) => {
  const [collapseOpen, setCollapseOpen] = useState(false);

  const history = useHistory();
  const { user, logout } = useAuth();
  const { darkMode } = useTheme();
  // PO1-3506: a tela atual tem tutorial? Então o ícone "?" existe e, no
  // celular, o cabeçalho reserva espaço para ele ao lado do sino.
  const temAjudaTela = Boolean(useTutorialDaTela());
  const {
    empresa,
    optionsEmpresa,
    transportadora,
    optionsTransportadora,
    loadCompanies,
    selectEmpresa,
    selectTransportadora,
  } = useCompanies();

  useEffect(() => {
    loadCompanies(user);
  }, [loadCompanies, user]);

  const handleSelectEmpresa = useCallback(
    (empresa) => {
      // Clique no X (isClearable) chega com `null`. Para admin Everlog, isso
      // significa "voltar à visão global", então casamos com a opção "Todos
      // os embarcadores" em vez de deixar a seleção vazia (que bloquearia
      // telas como /app/acessos/users).
      if (empresa == null) {
        const todos = (optionsEmpresa || []).find((opt) => opt?.isAllOption);
        selectEmpresa(todos || null);
        return;
      }
      selectEmpresa(empresa);
    },
    [optionsEmpresa, selectEmpresa]
  );

  const handleSelectTransportadora = useCallback(
    (transportadora) => {
      selectTransportadora(transportadora);
    },
    [selectTransportadora]
  );

  const handleLogout = useCallback(() => {
    logout();
  }, [logout]);

  const showDateFilterButton = DATE_FILTER_ENABLED_PATHS.has(
    props.location.pathname
  );
  const selectThemeClass = SELECT_THEME_CLASS[props.activeColor] || "info";

  // PO1-3470: o cabeçalho segue o tema mesmo com o menu "⋮" aberto (antes
  // virava bg-white também no escuro), então a cor dos selects depende só dele.
  const isLightNavbar = darkMode === false;
  const selectTextColor = isLightNavbar ? "#1e1e2f" : "#ffffff";
  const selectPlaceholderColor = isLightNavbar
    ? "rgba(30, 30, 47, 0.55)"
    : "rgba(255, 255, 255, 0.55)";
  const selectBorderColor = isLightNavbar
    ? "rgba(34, 42, 66, 0.2)"
    : "rgba(255, 255, 255, 0.16)";
  const selectBackgroundColor = isLightNavbar
    ? "rgba(255, 255, 255, 0.9)"
    : "rgba(255, 255, 255, 0.04)";
  const selectAccentColor = "var(--theme-accent, #1d8cf8)";
  const selectAccentStrongColor = "var(--theme-accent-strong, #3358f4)";
  const selectAccentGlow = "var(--theme-accent-glow, rgba(29, 140, 248, 0.18))";
  const selectValueColor = isLightNavbar
    ? selectAccentStrongColor
    : selectAccentColor;
  const navbarSelectStyles = {
    control: (base, state) => ({
      ...base,
      backgroundColor: selectBackgroundColor,
      borderColor: state.isFocused ? selectAccentColor : selectBorderColor,
      boxShadow: state.isFocused ? `0 0 0 3px ${selectAccentGlow}` : "none",
    }),
    input: (base) => ({
      ...base,
      color: selectTextColor,
      input: {
        color: selectTextColor,
      },
    }),
    placeholder: (base) => ({
      ...base,
      color: selectPlaceholderColor,
    }),
    singleValue: (base) => ({
      ...base,
      color: selectValueColor,
      fontWeight: 600,
    }),
    dropdownIndicator: (base) => ({
      ...base,
      color: selectPlaceholderColor,
    }),
    clearIndicator: (base) => ({
      ...base,
      color: selectPlaceholderColor,
    }),
  };
  const canClearEmpresaSelection = Boolean(
    optionsEmpresa?.some((option) => option?.isAllOption)
  );

  // PO1-3470: abrir o menu "⋮" só liga navbar-menu-aberto. O fundo vem do
  // tema em AdminNavbar.css, por media query (< 992px). Sai o bg-white do
  // template, que pintava o cabeçalho de branco também no tema escuro (título
  // e sino sumiam), e o listener de resize que era somado a cada abertura.
  const toggleCollapse = () => {
    setCollapseOpen(!collapseOpen);
  };
  return (
    <>
      <Navbar
        className={classNames({
          "navbar-transparent":
            props.location.pathname.indexOf("full-screen-map") === -1,
          "navbar-menu-aberto": collapseOpen,
        })}
        expand="lg"
      >
        <Container fluid>
          <div className={classNames("navbar-wrapper", { "com-ajuda-tela": temAjudaTela })}>
            <div className="navbar-minimize d-inline">
              <Button
                className="minimize-sidebar btn-just-icon"
                color="link"
                id="tooltip209599"
                onClick={props.handleMiniClick}
              >
                <i className="tim-icons icon-align-center visible-on-sidebar-regular" />
                <i className="tim-icons icon-align-left-2 visible-on-sidebar-mini" />
              </Button>
              <UncontrolledTooltip
                delay={0}
                target="tooltip209599"
                placement="right"
              >
                Recolher menu lateral
              </UncontrolledTooltip>
            </div>
            <div
              className={classNames("navbar-toggle d-inline", {
                toggled: props.sidebarOpened,
              })}
            >
              <button
                className="navbar-toggler"
                type="button"
                onClick={props.toggleSidebar}
              >
                <span className="navbar-toggler-bar bar1" />
                <span className="navbar-toggler-bar bar2" />
                <span className="navbar-toggler-bar bar3" />
              </button>
            </div>
            {/* Sprint 638: div em vez de <a href="#pablo">: o nome do produto
                virou botão (troca de produto) e botão dentro de link é HTML
                inválido; o link não levava a lugar nenhum. */}
            <NavbarBrand tag="div">
              {/* PO1-1974: breadcrumb 'Produto › Grupo › Tela' substitui o
                  brandText simples. Fallback pro brandText quando não há
                  produto ativo (setor=transportadora ou loading). */}
              <ProductBreadcrumb fallback={props.brandText} />
            </NavbarBrand>
          </div>
          {/* PO1-2194: no celular o sino fica SEMPRE à vista, fora do collapse,
              logo antes do "⋮". Em desktop ele mora no <Nav> da direita; cada
              variante só se monta na sua faixa de largura. */}
          <div className="alertas-sino-mobile-wrap d-lg-none">
            {/* PO1-3506: "Como usar esta tela", só quando a tela tem tutorial. */}
            <AjudaTela variant="mobile" />
            <AlertasSino variant="mobile" />
            <button
              className="navbar-toggler"
              type="button"
              data-toggle="collapse"
              data-target="#navigation"
              aria-expanded="false"
              aria-label="Toggle navigation"
              onClick={toggleCollapse}
            >
              <span className="navbar-toggler-bar navbar-kebab" />
              <span className="navbar-toggler-bar navbar-kebab" />
              <span className="navbar-toggler-bar navbar-kebab" />
            </button>
          </div>
          <Collapse navbar isOpen={collapseOpen}>
            <Col>
              <Select
                className={`react-select ${selectThemeClass}`}
                classNamePrefix="react-select"
                name="selectEmpresa"
                value={empresa}
                onChange={(value) => handleSelectEmpresa(value)}
                options={optionsEmpresa}
                placeholder="Embarcadores"
                isClearable={canClearEmpresaSelection}
                styles={navbarSelectStyles}
              />
            </Col>
            <Col>
              {user?.setor === "transportadora" ? (
                // PO1-2286: a transportadora do login fica fixa (raiz de CNPJ
                // no servidor); ela só escolhe o embarcador.
                <div
                  className="navbar-transportadora-fixa"
                  title="Transportadora do seu login"
                  style={{ fontSize: "0.85rem", padding: "0.45rem 0.25rem", color: "var(--everlog-card-title, inherit)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                >
                  <i className="tim-icons icon-delivery-fast" style={{ marginRight: 6 }} />
                  {user.transportadoraNome || (transportadora && transportadora.label) || "Transportadora"}
                </div>
              ) : (
                <Select
                  className={`react-select ${selectThemeClass}`}
                  classNamePrefix="react-select"
                  name="selectTransportadora"
                  value={transportadora || null}
                  onChange={(value) => handleSelectTransportadora(value)}
                  options={optionsTransportadora}
                  placeholder="Transportadoras"
                  isClearable={true} // habilita o botão de limpar
                  styles={navbarSelectStyles}
                />
              )}
            </Col>
            <Nav className="ml-auto" navbar>
              <UncontrolledDropdown nav>
                <DropdownToggle
                  caret
                  color="default"
                  data-toggle="dropdown"
                  nav
                >
                  <div className="caret d-none d-lg-block d-xl-block" />
                  <i className="tim-icons icon-bullet-list-67" />
                  <p className="d-lg-none">Trocar produto</p>
                </DropdownToggle>
                <DropdownMenu
                  className="dropdown-navbar dropdown-app-switcher"
                  right
                  tag="ul"
                >
                  <DropdownItem header>Trocar Para</DropdownItem>
                  {/* App Switcher (PO1-2059) — produtos contratados pelo
                      embarcador; render como items do dropdown. */}
                  <AppSwitcher />
                </DropdownMenu>
              </UncontrolledDropdown>
              {/* PO1-3506: ícone "?" (Como usar esta tela), ao lado do sino. */}
              <AjudaTela variant="desktop" className="d-none d-lg-block" />
              {/* Sino de alertas (PO1-2194), visível para todos os setores. */}
              <AlertasSino variant="desktop" className="d-none d-lg-block" />
              {/* Avatar dropdown (PO1-1976) — funções do usuário logado:
                  Meu perfil, Preferências, Sair. */}
              <UncontrolledDropdown nav>
                <DropdownToggle
                  caret
                  color="default"
                  data-toggle="dropdown"
                  nav
                  onClick={(e) => e.preventDefault()}
                >
                  <div className="photo">
                    <UserAvatar user={user} size={36} />
                  </div>
                  <b className="caret d-none d-lg-block d-xl-block" />
                  <p className="d-lg-none">Conta</p>
                </DropdownToggle>
                <DropdownMenu
                  className="dropdown-navbar dropdown-app-switcher"
                  right
                  tag="ul"
                >
                  <NavLink tag="li">
                    <DropdownItem
                      tag="a"
                      href="#"
                      className="app-switcher-item"
                      onClick={(e) => {
                        e.preventDefault();
                        history.push("/app/minha-conta/perfil");
                      }}
                    >
                      <span className="app-switcher-item__icon">
                        <i className="tim-icons icon-single-02" />
                      </span>
                      Meu perfil
                    </DropdownItem>
                  </NavLink>
                  <NavLink tag="li">
                    <DropdownItem
                      tag="a"
                      href="#"
                      className="app-switcher-item"
                      onClick={(e) => {
                        e.preventDefault();
                        history.push("/app/minha-conta/preferencias");
                      }}
                    >
                      <span className="app-switcher-item__icon">
                        <i className="tim-icons icon-settings-gear-63" />
                      </span>
                      Preferências
                    </DropdownItem>
                  </NavLink>
                  <DropdownItem divider tag="li" />
                  <NavLink tag="li">
                    <DropdownItem
                      tag="a"
                      href="#"
                      className="app-switcher-item"
                      onClick={(e) => {
                        e.preventDefault();
                        handleLogout();
                      }}
                    >
                      <span className="app-switcher-item__icon">
                        <i className="tim-icons icon-button-power" />
                      </span>
                      Sair
                    </DropdownItem>
                  </NavLink>
                </DropdownMenu>
              </UncontrolledDropdown>
              {showDateFilterButton && (
                <>
                  <li className="separator d-lg-none" />
                  <NavItem>
                    <DateFilterButton />
                  </NavItem>
                </>
              )}
            </Nav>
          </Collapse>
        </Container>
      </Navbar>
    </>
  );
};

export default AdminNavbar;
